/**
 * Shared focus-trap stack for modals, drawers, and floating menus.
 */

import { getTabbables } from './tabbable.js';

const _trapStack = [];
let _globalEscWired = false;

function _topOfTrapStack() {
  return _trapStack[_trapStack.length - 1] || null;
}

/** Whether `container` currently owns the active trap. Deferred focus must
 * never move into a dialog underneath a newer one. */
export function isTopFocusTrap(container) {
  return _topOfTrapStack()?.container === container;
}

function _wireGlobalEscOnce() {
  if (_globalEscWired) return;
  _globalEscWired = true;

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const top = _topOfTrapStack();
      if (!top) return;
      if (top.container.contains(document.activeElement)) return;
      event.stopPropagation();
      event.preventDefault();
      top.container.dispatchEvent(new Event('trap-escape'));
    },
    true
  );
}

export function isAnyModalOpen() {
  return _trapStack.length > 0;
}

/**
 * Resolve and apply the initial focus for a trapped container (PP-0).
 *
 * `initialFocus` is one of: undefined (the first focusable — the behaviour every
 * caller had before the option existed), `false` (focus nothing), a selector
 * string (resolved against the container), an element, or a function returning
 * any of those — the function form exists because the target usually does not
 * exist yet when the trap is armed (createModal traps an EMPTY body). Anything
 * that resolves outside the container falls back to the first focusable: the
 * trap must never hand focus to something it does not contain.
 *
 * @returns {Element|null} the element focused, or null when nothing was
 */
export function focusInitialIn(container, initialFocus) {
  let target = typeof initialFocus === 'function' ? initialFocus() : initialFocus;
  if (target === false) return null;
  if (typeof target === 'string') target = container.querySelector(target);
  // Duck-typed on purpose: the jsdom suites install `document` but not a
  // global `Element`, and a node is a node whichever realm built it.
  const isNode = !!target && target.nodeType === 1 && typeof target.focus === 'function';
  if (!isNode || !container.contains(target)) {
    // BC-4: the first control that is actually on screen, never one inside a
    // hidden tab panel.
    target = getTabbables(container)[0] || null;
  }
  if (!target) return null;
  target.focus();
  return target;
}

export function trapFocus(container, { initialFocus } = {}) {
  _trapStack.push({ container });
  _wireGlobalEscOnce();

  function handler(event) {
    if (event.key === 'Escape') {
      if (_topOfTrapStack()?.container !== container) return;
      event.stopPropagation();
      container.dispatchEvent(new Event('trap-escape'));
      return;
    }
    if (event.key !== 'Tab') return;
    // BC-4: the ring is what Tab would actually visit. A selector-only list
    // counted controls inside hidden tab panels, so "first" was an invisible
    // control, the wrap never triggered, and Shift+Tab from Close stuck.
    const focusable = getTabbables(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  container.addEventListener('keydown', handler);
  focusInitialIn(container, initialFocus);

  return () => {
    container.removeEventListener('keydown', handler);
    const idx = _trapStack.findIndex((t) => t.container === container);
    if (idx >= 0) _trapStack.splice(idx, 1);
  };
}
